import {useEffect, useRef, useState} from 'react'
import {useDialogContext, useDialogStore} from '@ariakit/react'
import {AxiosError} from 'axios'
import {
    Dialog,
    DialogDisclosure,
    DialogDismiss,
    DialogHeader,
    DialogHeading,
    DialogProvider
} from '@/components/ui/dialog-atoms/DialogAtoms'
import {Button} from '@/components/ui/button/Button'
import {PlusIcon} from '@/components/ui/icon/Icon'
import {useTranslation} from '@/translations/i18n'
import {toast} from '@/utilities/toast/toast'
import {formatLocaleDate} from '@/dayjs'
import {capitalize} from '@/utilities/helpers'
import {usePermissions} from '@/features/auth/store/store'
import {ActivityReportError, ActivityReportSector} from '@/features/accesses/activity-report/types'
import {activityReportSectorToLabel, resolveActivityReportErrorLabel} from '@/features/accesses/activity-report/utils'
import {useGetActivityReportCounters} from '@/features/accesses/activity-report/queries/useGetActivityReportCounters'
import {useStoreActivityReport} from '@/features/accesses/activity-report/queries/useStoreActivityReport'
import {flyToExportsToggle} from '@/components/commons/export-modal/flyToExports'
import {ExportJob} from '@/features/exports/types'
import {useExportsIndicatorStore} from '@/features/exports/hooks/useExportsIndicator'
import {useExportAnnouncementStore} from '@/features/exports/hooks/useExportAnnouncement'
import {ActivityReportConfigStep, ActivityReportPeriod} from './ActivityReportConfigStep'
import {ActivityReportResultStep} from './ActivityReportResultStep'

export const ActivityReportDialog = () => {
    const {t} = useTranslation()
    const permissions = usePermissions()
    const [isOpen, setIsOpen] = useState(false)
    const dialogStore = useDialogStore({open: isOpen, setOpen: setIsOpen})

    if (!permissions?.projects?.write) {
        return null
    }

    return (
        <DialogProvider store={dialogStore}>
            <DialogDisclosure
                render={
                    <Button variant="primaryNeutral" onClick={() => setIsOpen(true)}>
                        <PlusIcon />
                        {t('activity_report:new_report')}
                    </Button>
                }
            />
            {isOpen && <ActivityReportDialogShell onClose={() => dialogStore.setOpen(false)} />}
        </DialogProvider>
    )
}

const ActivityReportDialogShell = ({onClose}: {onClose: () => void}) => {
    const {t} = useTranslation()
    const dialog = useDialogContext()
    const [step, setStep] = useState<'config' | 'result'>('config')
    const [period, setPeriod] = useState<ActivityReportPeriod | null>(null)
    const [sector, setSector] = useState<ActivityReportSector | null>(null)
    const hasFlownRef = useRef(false)
    const enqueuedJobRef = useRef<ExportJob | null>(null)

    const bothSelected = !!period && !!sector

    const countersQuery = useGetActivityReportCounters(
        {startDate: period?.startDate ?? '', endDate: period?.endDate ?? '', sector: sector ?? 'outpatient'},
        {enabled: bothSelected}
    )

    const mutation = useStoreActivityReport({
        onSuccess: report => {
            // The enqueued job now lives in the header exports toggle. Reshape the embedded job into
            // the shared ExportJob the toggle stores consume (it omits entity/requestedAt); the real
            // entity and file fields arrive later via the `export:status_changed` socket event.
            const job = report.exportJob
            if (!job) return
            const exportJob: ExportJob = {
                id: job.id,
                entity: 'activity_report',
                status: job.status,
                format: job.format ?? 'xlsx',
                requestedAt: report.createdAt,
                downloadUrl: job.downloadUrl,
                fileName: job.fileName
            }
            // Light up the toggle dot immediately (orange) without waiting for the first socket event.
            useExportsIndicatorStore.getState().track(exportJob.id, exportJob.status)
            // Give the header popover a report-specific title so its ready/failed states read
            // "Rendiconto {sector} di {month} {year} pronto/fallito" instead of the generic export
            // phrase. Registered now so it survives the pending → ready/failed transition the socket
            // drives (the generic ExportJob it announces carries no period/sector context).
            const period = capitalize(formatLocaleDate(report.startDate, 'MMMM YYYY'))
            const sector = t(activityReportSectorToLabel[report.sector])
            useExportAnnouncementStore.getState().registerTitleOverride(exportJob.id, {
                ready: t('activity_report:announce_ready_title', {sector, period}),
                failed: t('activity_report:announce_failed_title', {sector, period})
            })
            // Stash it so the fly animation can announce it on the toggle once it lands.
            enqueuedJobRef.current = exportJob
        },
        onError: (error: AxiosError<ActivityReportError>) => {
            toast.error(t(resolveActivityReportErrorLabel(error.response?.data?.code)))
        }
    })

    // On a successful enqueue, "crumple" the dialog and fly it into the header exports button (the
    // export-history toggle), then close it — mirroring the generic async-export flow. With reduced
    // motion, a missing anchor, or no enqueued job we fall back to the static success confirmation.
    useEffect(() => {
        if (!mutation.isSuccess || hasFlownRef.current) return
        hasFlownRef.current = true
        if (!enqueuedJobRef.current) {
            setStep('result')
            return
        }
        const dialogElement = dialog?.getState().contentElement ?? null
        void flyToExportsToggle(dialogElement).then(played => {
            if (!played) {
                setStep('result')
                return
            }
            // Right after the dialog lands on the toggle, announce the pending job there; the popover
            // then transitions to ready/failed when the socket reports the outcome. Guard against a
            // fast export whose socket result already arrived during the ~700ms animation.
            const pendingJob = enqueuedJobRef.current
            const announced = useExportAnnouncementStore.getState().job
            if (pendingJob && announced?.id !== pendingJob.id) {
                useExportAnnouncementStore.getState().announce(pendingJob)
            }
            onClose()
        })
    }, [mutation.isSuccess, dialog, onClose])

    const handleGenerate = () => {
        if (!period || !sector) return
        mutation.mutate({startDate: period.startDate, endDate: period.endDate, sector})
    }

    return (
        <Dialog width="560px">
            <DialogHeader>
                <DialogHeading>{t('activity_report:dialog_title')}</DialogHeading>
                <DialogDismiss />
            </DialogHeader>
            {step === 'config' && (
                <ActivityReportConfigStep
                    period={period}
                    sector={sector}
                    onPeriodApply={setPeriod}
                    onSectorChange={setSector}
                    counters={countersQuery.data}
                    isCountersLoading={bothSelected && countersQuery.isLoading}
                    isCountersError={bothSelected && countersQuery.isError}
                    isGenerating={mutation.isPending}
                    onGenerate={handleGenerate}
                />
            )}
            {step === 'result' && (
                <ActivityReportResultStep
                    variant="success"
                    title={t('activity_report:success_title')}
                    subtitle={t('activity_report:success_subtitle')}
                    onClose={onClose}
                />
            )}
        </Dialog>
    )
}
